import { apiInstance } from '@/api/instance';

/** Contract: bodyprint-api/docs/external-apis/partners-contract.md (Conta). */
export type PartnerStatus = 'pending_review' | 'approved' | 'rejected' | 'suspended';

export type PartnerMemberRole = 'owner' | 'editor' | 'viewer';

export type PartnerCategory =
  | 'events'
  | 'running_club'
  | 'bike_fit'
  | 'nutrition'
  | 'physio'
  | 'gear'
  | 'sports_media'
  | 'other';

export interface Partner {
  id: string;
  displayName: string;
  legalName: string;
  /** CPF (11) or CNPJ (14) digits. */
  taxId: string;
  category: PartnerCategory;
  website: string | null;
  instagram: string | null;
  description: string | null;
  logoUrl: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  city: string | null;
  state: string | null;
  status: PartnerStatus;
  /** Consent provider served as a sports media platform; set by the admin with the first key. */
  mediaProvider: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  origin: 'portal' | 'admin';
  /** Someone took an admin-made account over through the claim invite. */
  claimedAt: string | null;
  termsAcceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerAccountResponse {
  partner: Partner | null;
  role: PartnerMemberRole | null;
}

/** Fields the owner writes (POST signup and PUT me). */
export type PartnerAccountBody = Pick<
  Partner,
  'displayName' | 'legalName' | 'taxId' | 'category' | 'contactName' | 'contactEmail'
> &
  Partial<
    Pick<
      Partner,
      'website' | 'instagram' | 'description' | 'logoUrl' | 'contactPhone' | 'city' | 'state'
    >
  >;

export type CreatePartnerAccountRequest = PartnerAccountBody & { acceptTerms: true };

/** One line of the account timeline (`GET /partners/me/history`); decisions never name who decided. */
export type PartnerAccountHistoryType =
  'submitted' | 'registered' | 'claimed' | 'approved' | 'rejected' | 'suspended' | 'reactivated';

interface PartnerAccountHistoryItem {
  id: string;
  type: PartnerAccountHistoryType;
  note: string | null;
  createdAt: string;
}

export interface PartnerAccountHistoryResponse {
  items: PartnerAccountHistoryItem[];
}

export const partnersApi = {
  me: () => apiInstance.get<PartnerAccountResponse>('partners/me'),
  history: () => apiInstance.get<PartnerAccountHistoryResponse>('partners/me/history'),
  signup: (data: CreatePartnerAccountRequest) =>
    apiInstance.post<PartnerAccountResponse>('partners/signup', data),
  update: (data: PartnerAccountBody) =>
    apiInstance.put<PartnerAccountResponse>('partners/me', data),
};

export const partnersApiKeys = {
  all: ['partners'] as const,
  me: () => ['partners', 'me'] as const,
  history: () => ['partners', 'me', 'history'] as const,
};
